/**
 * The Wiring step's circuits: the choices for the conductor schedule and the point of interconnection (code edition,
 * interconnection method, meter and main, disconnect, raceway, rooftop height, voltage-drop target, three-line
 * sheet), saved with the site details (`electrical`), and the tagged conductor and conduit schedule as sheets E-1
 * and E-2 print it, with a size and a length to enter per run. lib/permitWiring.ts holds the pure parts.
 */
import { Badge, Card, Field, NumberInput, Select, Switch } from "../components/ui";
import type { Country } from "../lib/permits";
import {
  AWG_SIZES,
  CONDUIT_OPTIONS,
  DISCONNECT_OPTIONS,
  EDITION_OPTIONS,
  INTERCONNECTION_OPTIONS,
  MM2_SIZES,
  changedCount,
  lengthLabel,
  lengthToMetres,
  withElectrical,
  withRun,
  type ConductorRole,
  type ElectricalInputs,
} from "../lib/permitWiring";
import { useT } from "../i18n/I18nProvider";
import type { Ws } from "./PermitWorkspace";
import { useFormat } from "./format";

interface Row {
  tag: number;
  role: string;
  name: string;
  conductors: string;
  size: string;
  egc: string;
  raceway: string;
  length_m: number;
  length_source: string;
  vd_pct: number;
  status: string;
  entered_size?: boolean;
}

export function WiringSchedule({ ws }: { ws: Ws }) {
  const t = useT();
  const f = useFormat();
  const wiring = ws.preview.data?.electrical.wiring as (Record<string, unknown> & { schedule: Row[] }) | null | undefined;
  const country = (ws.inputs.country ?? (ws.preview.data?.country as Country | undefined)) || "US";
  const e = (ws.details.electrical ?? {}) as ElectricalInputs;
  const off = !ws.canEdit;
  const set = (patch: Partial<ElectricalInputs>) => ws.setDetails((d) => withElectrical(d as { electrical?: ElectricalInputs | null }, patch) as typeof d);
  const setRun = (role: string, patch: { size?: string | null; mm2?: number | null; length_m?: number | null }) =>
    ws.setDetails((d) => withRun(d as { electrical?: ElectricalInputs | null }, role as ConductorRole, patch) as typeof d);
  const nec = country === "US" || country === "CA" || country === "MX";
  const metricCables = country === "GB" || country === "AU";
  const units = ws.units;
  const target = e.voltage_drop_target_pct ?? 2;
  const edition = (wiring?.code_edition as { name?: string; source?: string } | null | undefined) ?? null;
  const count = changedCount(e);

  return (
    <>
      <Card
        title={t("permit.schedule.circuits.title")}
        description={t("permit.schedule.circuits.description")}
        actions={count ? <Badge tone="info">{t("permit.schedule.circuits.changed", { count })}</Badge> : undefined}
      >
        <div className="pw-form-grid" data-anchor="circuits">
          {country === "US" && (
            <Field label={t("permit.schedule.codeEdition")} hint={edition?.name ? t("permit.schedule.codeEditionSource", { name: edition.name, source: edition.source ?? "" }) : undefined}>
              <Select value={e.nec_edition ? String(e.nec_edition) : "auto"} disabled={off} ariaLabel={t("permit.schedule.codeEdition")} options={EDITION_OPTIONS.map((x) => ({ value: x.value, label: t(x.label) }))}
                onChange={(v) => set({ nec_edition: v === "auto" ? null : (Number(v) as 2017 | 2020 | 2023) })} />
            </Field>
          )}
          {nec && (
            <Field label={t("permit.schedule.interconnection")} hint={e.interconnection === "supply_side" ? t("permit.schedule.interconnectionHint.supplySide") : e.interconnection === "load_side_derate" ? undefined : t("permit.schedule.interconnectionHint.loadSide")}>
              <Select value={e.interconnection ?? "load_side"} disabled={off} ariaLabel={t("permit.schedule.interconnection")} options={INTERCONNECTION_OPTIONS.map((x) => ({ value: x.value, label: t(x.label) }))}
                onChange={(v) => set({ interconnection: v === "load_side" ? null : v })} />
            </Field>
          )}
          {nec && e.interconnection === "load_side_derate" && (
            <Field label={t("permit.schedule.deratedMain")} hint={t("permit.schedule.deratedMainHint")}>
              <NumberInput value={e.main_breaker_derate_a ?? null} disabled={off} suffix="A" min={15} max={2000} placeholder={t("permit.schedule.deratedMainPlaceholder", { value: 175 })} onChange={(v) => set({ main_breaker_derate_a: v })} />
            </Field>
          )}
          <Field label={t("permit.schedule.meterMain")}>
            <Select value={e.meter_main ?? "separate"} disabled={off} ariaLabel={t("permit.schedule.meterMain")}
              options={[{ value: "separate", label: t("permit.schedule.meterMain.separate") }, { value: "combination", label: t("permit.schedule.meterMain.combination") }]}
              onChange={(v) => set({ meter_main: v === "separate" ? null : v })} />
          </Field>
          {!metricCables && (
            <>
              <Field label={t("permit.schedule.acDisconnect")} hint={!e.ac_disconnect || e.ac_disconnect === "auto" ? t("permit.schedule.acDisconnectHint") : undefined}>
                <Select value={e.ac_disconnect ?? "auto"} disabled={off} ariaLabel={t("permit.schedule.acDisconnect")} options={DISCONNECT_OPTIONS.map((x) => ({ value: x.value, label: t(x.label) }))}
                  onChange={(v) => set({ ac_disconnect: v === "auto" ? null : v })} />
              </Field>
              <Field label={t("permit.schedule.raceway")}>
                <Select value={e.conduit ?? "emt"} disabled={off} ariaLabel={t("permit.schedule.raceway")} options={CONDUIT_OPTIONS.map((x) => ({ value: x.value, label: t(x.label) }))}
                  onChange={(v) => set({ conduit: v === "emt" ? null : v })} />
              </Field>
            </>
          )}
          <Field label={t("permit.schedule.vdTarget")} hint={t("permit.schedule.vdTargetHint")}>
            <NumberInput value={e.voltage_drop_target_pct ?? null} disabled={off} suffix="%" min={0.5} max={5} placeholder={f.num(2)} onChange={(v) => set({ voltage_drop_target_pct: v })} />
          </Field>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          {!metricCables && (
            <Switch checked={!!e.rooftop_low} disabled={off} onChange={(v) => set({ rooftop_low: v || null })}
              label={t("permit.schedule.rooftopLow")} />
          )}
          <Switch checked={!!e.three_line} disabled={off} onChange={(v) => set({ three_line: v || null })} label={t("permit.schedule.threeLine")} />
        </div>
      </Card>

      <Card title={t("permit.schedule.table.title")} description={t("permit.schedule.table.description", { target })}>
        {!wiring?.schedule?.length ? (
          <p className="small muted" style={{ margin: 0 }}>{t("permit.schedule.table.empty")}</p>
        ) : (
          <div className="pw-table-wrap" data-anchor="schedule">
            <table className="pw-table pw-schedule">
              <thead>
                <tr>
                  <th>{t("permit.schedule.col.tag")}</th>
                  <th>{t("permit.schedule.col.circuit")}</th>
                  <th>{t("permit.schedule.col.conductors")}</th>
                  <th>{t("permit.schedule.col.egc")}</th>
                  <th>{t("permit.schedule.raceway")}</th>
                  <th>{t("permit.schedule.col.size")}</th>
                  <th>{t("permit.schedule.col.length")}</th>
                  <th className="num">{t("permit.schedule.col.voltageDrop")}</th>
                </tr>
              </thead>
              <tbody>
                {wiring.schedule.map((r) => {
                  const run = e.tags?.[r.role as ConductorRole] ?? {};
                  const sizeOptions = metricCables
                    ? [{ value: "auto", label: t("permit.wiring.automatic") }, ...MM2_SIZES.map((m) => ({ value: String(m), label: t("permit.schedule.mm2", { value: m }) }))]
                    : [{ value: "auto", label: t("permit.wiring.automatic") }, ...AWG_SIZES.map((s) => ({ value: s, label: s }))];
                  const sizeValue = metricCables ? (run.mm2 != null ? String(run.mm2) : "auto") : run.size ?? "auto";
                  const shown = run.length_m != null ? (units === "imperial" ? Math.round(run.length_m / 0.3048) : run.length_m) : null;
                  return (
                    <tr key={r.role} className={r.status === "problem" ? "pw-row-problem" : undefined}>
                      <td><span className="pw-tag">{r.tag}</span></td>
                      <td>{r.name}</td>
                      <td>{r.conductors}</td>
                      <td className="small">{r.egc}</td>
                      <td className="small">{r.raceway}</td>
                      <td>
                        {r.role === "ac_trunk" ? <span className="small muted">{t("permit.schedule.manufacturerCable")}</span> : (
                          <Select value={sizeValue} disabled={off} ariaLabel={t("permit.schedule.sizeOfTag", { tag: r.tag })} options={sizeOptions}
                            onChange={(v) => setRun(r.role, metricCables ? { mm2: v === "auto" ? null : Number(v) } : { size: v === "auto" ? null : v })} />
                        )}
                      </td>
                      <td>
                        <NumberInput value={shown} disabled={off} suffix={units === "imperial" ? "ft" : "m"} min={1} max={1600}
                          placeholder={units === "imperial" ? f.num(Math.round(r.length_m / 0.3048)) : f.num(r.length_m, 1)}
                          onChange={(v) => setRun(r.role, { length_m: lengthToMetres(v, units) })} />
                        <div className="small muted">{lengthLabel(t, r.length_m, r.length_source, units)}</div>
                      </td>
                      <td className="num">
                        <Badge tone={r.status === "problem" ? "danger" : r.vd_pct > target * 0.75 ? "warn" : "ok"}>{t("permit.schedule.vdPct", { value: r.vd_pct })}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
